/**
 * `sizes` strings for CMS media, in one place.
 *
 * WHY THIS EXISTS: an `<Image>` with a numeric `width` and no `sizes` takes
 * Next's x-descriptor path, which emits a SINGLE candidate at the largest
 * `deviceSizes` entry regardless of how small the image renders. With the
 * ladder now reaching 2560, a missing `sizes` means a 380px thumbnail
 * downloads a 2560px file. Every content image must pass one of these.
 *
 * Each string encodes real geometry, so update it when the CSS changes:
 *   --container-padding-x  24 / 32 / 48 at 0 / 768 / 1024   (tokens.css:12, globals.css:178,185)
 *   --column-gap-md        32px, the CardSection default     (CardSection.tsx)
 *   .section-container     max-width 1024 (w-lg 1176, w-xl 1440)
 *
 * NOTE the two grid systems use DIFFERENT breakpoints:
 *   cardsGrid.module.css  goes multi-column at 768
 *   CardSection.module.css bespoke 2-up grids go multi-column at 1024
 * Mixing them up under-fetches on tablets, hence the separate constants.
 *
 * Each string deliberately keeps a small-`vw` clause: Next filters the ladder
 * to `s >= deviceSizes[0] * smallestVwRatio`, so the smallest vw in the
 * string decides which low rungs stay reachable.
 */

/** Hero card: .heroCard max-width 1280 − 2×48 padding (CardSection.module.css). */
export const CARD_SIZES_HERO =
  "(min-width: 1280px) 1280px, (min-width: 768px) calc(100vw - 96px), 100vw";

/** Full-width single card (1-up, 1up100vh). */
export const CARD_SIZES_1UP = CARD_SIZES_HERO;

/** 2-up 50/50 inside CardsGrid — multi-column at 768. */
export const CARD_SIZES_2UP_GRID =
  "(min-width: 1280px) 592px, (min-width: 768px) calc(50vw - 64px), 100vw";

/** 2-up 50/50 inside CardSection's bespoke grid — multi-column at 1024. */
export const CARD_SIZES_2UP_SECTION =
  "(min-width: 1280px) 592px, (min-width: 1024px) calc(50vw - 64px), (min-width: 768px) calc(100vw - 64px), 100vw";

/** The 3fr column of .twoUp6040Grid / .twoUp4060Grid. */
export const CARD_SIZES_2UP_60 =
  "(min-width: 1280px) 710px, (min-width: 1024px) calc(60vw - 72px), (min-width: 768px) calc(100vw - 64px), 100vw";

/** The 2fr column of the same grids. */
export const CARD_SIZES_2UP_40 =
  "(min-width: 1280px) 474px, (min-width: 1024px) calc(40vw - 56px), (min-width: 768px) calc(100vw - 64px), 100vw";

/** 3-up grid, and .centerColumn (max 1200 / 3 columns). The lean default. */
export const CARD_SIZES_3UP =
  "(min-width: 1280px) 400px, (min-width: 1024px) calc(33vw - 40px), (min-width: 768px) calc(50vw - 40px), 100vw";

/** 4-up: 2 columns below 1024, 4 at 1024+ (cardsGrid.module.css). */
export const CARD_SIZES_4UP =
  "(min-width: 1280px) 280px, (min-width: 1024px) calc(25vw - 32px), 50vw";

/** .carouselItemSm is flex: 0 0 72vw and only renders below 768. */
export const CARD_SIZES_CAROUSEL = "72vw";

/**
 * MinimalDialog content is `width: min(100vw - 48px, 720px)`, but this asks
 * for 1440 — deliberately 2x the layout width.
 *
 * The dialog shows page scans (resume templates and the like) cropped to their
 * top portion, so a 2550px-wide source is being squeezed into 720 CSS px. At a
 * plain 2x that is still a >40% downsample of dense body text, which reads
 * soft however good the encoder is. Over-requesting puts a 2560 candidate in
 * the srcset so retina displays get near-native resolution.
 *
 * This is the one place we intentionally over-fetch; it is a single image,
 * loaded only when the user opens the dialog.
 */
export const DIALOG_IMAGE_SIZES = "(min-width: 768px) 1440px, 100vw";

/** .galleryItem is max-width 220 / 260. */
export const GALLERY_ITEM_SIZES = "(min-width: 1024px) 260px, 220px";

/** .appsIcon is a fixed 60×60. */
export const GALLERY_APP_ICON_SIZES = "60px";
